import { InlineAlertAnimator, TabStyles, TextLinkWidget } from '@tableau/widgets';
import * as React from 'react';
import { classes, style } from 'typestyle';

import { getAppStore } from '~src/app/AppStore';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { Messages } from '~src/codegen/Localize';
import { RunFlowTaskStatus } from '~src/ts/models/RunFlowTaskInfo';

import { getRunFlowTaskStatusIconClass } from '../utils/FlowIconUtils';
import { localizeRunFlowTaskStatus } from '../utils/FlowStatusUtils';
import { ExpandButton, ExpandButtonProps } from './ExpandButton';

export const buttonSize = 24;

const flexAutoStyle = style({
  flex: '1 1 auto',
});

export const itemClass = style({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${TabStyles.Colors.L6}`,
  fontSize: '12px',
  paddingLeft: 6,
});

const expandedItemClass = style({
  backgroundColor: `${TabStyles.Colors.L2_5}`,
});

const obfuscatedClass = style({
  height: buttonSize,
  paddingRight: buttonSize,
});

const nameWrapper = style({
  display: 'flex',
  alignItems: 'center',
});

export const lastRunStyle = style({
  display: 'flex',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginLeft: 'auto',
});

const lastRunLabel = style({
  fontWeight: 'bold',
  marginRight: 8,
});

const statusInfoStyle = style({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 32,
  marginRight: 20,
});

const statusIcon = style({
  flexShrink: 0,
  width: 19,
  height: 19,
  marginRight: 4,
});

const statusText = style({
  marginRight: 6,
});

export const flowNameStyle = style({
  paddingLeft: 8,
});

const obfuscatedStyle = style({
  paddingLeft: 8,
  color: TabStyles.Typography.Color.PrimaryLight,
  fontStyle: 'italic',
});

export interface FlowTaskInfoItemProps {
  taskId: string;
  flowName: string;
  flowId: string;
  lastRun: Date | null;
  taskStatus: RunFlowTaskStatus;
  expansionContent?: React.ReactNode;
  startExpanded?: boolean;
  shouldObfuscate?: boolean;
}

export function FlowTaskInfoItem(props: FlowTaskInfoItemProps) {
  const [isExpanded, setIsExpanded] = React.useState(props.startExpanded);

  const siteUrls = siteUrlsSelector(getAppStore().getState());

  function statusInfo() {
    return (
      <div className={statusInfoStyle}>
        <div className={classes(getRunFlowTaskStatusIconClass(props.taskStatus), statusIcon)} />
        <div className={statusText}>{localizeRunFlowTaskStatus(props.taskStatus)}</div>
      </div>
    );
  }
  const ariaControlsId = `${props.taskId}-expansion`;
  const expandButtonProps: ExpandButtonProps = {
    isExpanded,
    setIsExpanded,
    ariaControlsId,
    style: {
      border: 0,
      width: buttonSize,
      height: buttonSize,
    }
  };
  return (
    <div className={flexAutoStyle}>
      <div className={classes(itemClass, isExpanded ? expandedItemClass : undefined, props.shouldObfuscate ? obfuscatedClass : undefined)}>
        <div className={nameWrapper}>
          {Messages.runFlow()}
          {props.shouldObfuscate ?
            <span className={obfuscatedStyle}>
              {Messages.linkedTasksPermissionsRequired()}
            </span>
            : <TextLinkWidget
              href={siteUrls.flowOverviewUrl(props.flowId)}
              testId={'FlowTaskInfoItemLink' + props.flowName}
              target={'_blank'}
              className={flowNameStyle}
            >{props.flowName}
            </TextLinkWidget>}
        </div>
        <div className={lastRunStyle}>
          <div className={lastRunLabel}>{Messages.lastRun()}</div>
          {props.lastRun ? props.lastRun.toLocaleString() : Messages.flowOverviewNeverRun()}
        </div>
        {statusInfo()}
        {props.shouldObfuscate ? null : <ExpandButton {...expandButtonProps} />}
      </div>
      <InlineAlertAnimator>
        {isExpanded && props.expansionContent &&
          <div id={ariaControlsId}>
            {props.expansionContent}
          </div>
        }
      </InlineAlertAnimator>
    </div>
  );
}
